import { normalizeCrmCreatedDateValue } from "./crmCreatedDateFilters";

/** Approved taxonomy §14: one shared contract for recorded Forms history. */
export const CRM_FORM_SUBMISSION_FIELDS = [
  { value: "form_submitted", label: "Form submitted" },
  { value: "form_submitted_date", label: "Form submitted date" },
  { value: "form_source", label: "Form source" },
  { value: "utm_source", label: "UTM source" },
  { value: "utm_campaign", label: "UTM campaign" },
  { value: "utm_medium", label: "UTM medium" },
] as const;

export type CrmFormSubmissionField =
  (typeof CRM_FORM_SUBMISSION_FIELDS)[number]["value"];

export const CRM_FORM_SOURCE_OPTIONS = [
  { value: "website", label: "Website" },
  { value: "in_app", label: "In-app" },
  { value: "partner_portal", label: "Partner portal" },
  { value: "campaign_landing_page", label: "Campaign landing page" },
] as const;

export type CrmFormSource = (typeof CRM_FORM_SOURCE_OPTIONS)[number]["value"];

export const CRM_FORM_SUBMISSION_CONDITIONS = ["is", "is_not"] as const;
export const CRM_FORM_DATE_CONDITIONS = [
  "is_within",
  "before",
  "after",
] as const;
export const CRM_FORM_UTM_CONDITIONS = ["is", "is_not", "contains"] as const;

export type CrmFormSubmissionCondition =
  | (typeof CRM_FORM_SUBMISSION_CONDITIONS)[number]
  | (typeof CRM_FORM_DATE_CONDITIONS)[number]
  | (typeof CRM_FORM_UTM_CONDITIONS)[number];

export const isCrmFormSubmissionField = (
  field: string,
): field is CrmFormSubmissionField =>
  CRM_FORM_SUBMISSION_FIELDS.some(({ value }) => value === field);

export const isCrmFormUtmField = (
  field: string,
): field is "utm_source" | "utm_campaign" | "utm_medium" =>
  field === "utm_source" || field === "utm_campaign" || field === "utm_medium";

/** Submitted/Not submitted select a persisted form ID, never its mutable name. */
export const normalizeCrmFormSubmissionValue = (
  field: string,
  condition: string,
  value: unknown,
): string | null => {
  if (field === "form_submitted_date")
    return CRM_FORM_DATE_CONDITIONS.some((entry) => entry === condition)
      ? normalizeCrmCreatedDateValue(condition, value)
      : null;
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  if (field === "form_submitted") {
    if (!CRM_FORM_SUBMISSION_CONDITIONS.some((entry) => entry === condition))
      return null;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu.test(
      normalized,
    )
      ? normalized.toLowerCase()
      : null;
  }
  if (field === "form_source")
    return CRM_FORM_SUBMISSION_CONDITIONS.some((entry) => entry === condition)
      ? (CRM_FORM_SOURCE_OPTIONS.find(
          ({ value: option }) => option === normalized.toLowerCase(),
        )?.value ?? null)
      : null;
  if (
    !isCrmFormUtmField(field) ||
    !CRM_FORM_UTM_CONDITIONS.some((entry) => entry === condition) ||
    normalized.length === 0 ||
    normalized.length > 200
  )
    return null;
  return normalized;
};
